import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';

const About: React.FC = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [factorySlide, setFactorySlide] = useState(0);
  
  const carouselImages = [
    '/images/about/about1.webp',
    '/images/about/about2.webp',
    '/images/about/about3.webp',
  ];

  const factoryImages = [
    '/images/factory/factory1.webp',
    '/images/factory/factory2.webp',
    '/images/factory/factory3.webp',
    '/images/factory/factory4.webp',
    '/images/factory/factory5.webp',
    '/images/factory/factory6.webp',
    '/images/factory/factory7.webp',
  ];

  // 自动轮播
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000); // 每4秒切换一次

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // 工厂图片自动轮播
  useEffect(() => {
    const interval = setInterval(() => {
      setFactorySlide((prev) => (prev + 1) % factoryImages.length);
    }, 3000); // 每3秒切换一次

    return () => clearInterval(interval);
  }, [factoryImages.length]);

  // 手动切换到指定幻灯片
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // 上一张
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // 下一张
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  // 工厂轮播控制函数
  const goToFactorySlide = (index: number) => {
    setFactorySlide(index);
  };

  const prevFactorySlide = () => {
    setFactorySlide((prev) => (prev - 1 + factoryImages.length) % factoryImages.length);
  };

  const nextFactorySlide = () => {
    setFactorySlide((prev) => (prev + 1) % factoryImages.length);
  };

  return (
    <main className="about-container">
      {/* 轮播图区域 */}
      <section className="carousel-section">
        <div className="carousel-container">
          <div className="carousel-wrapper">
            {carouselImages.map((image, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
              >
                <img src={image} alt={`${t('about.title')} ${index + 1}`} />
                <div className="carousel-overlay">
                  <div className="carousel-content">
                    <h1 className="carousel-title">
                      {t('about.carouselTitle')}
                    </h1>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* 轮播控制按钮 */}
          <button className="carousel-btn carousel-btn-prev" onClick={prevSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="carousel-btn carousel-btn-next" onClick={nextSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {/* 轮播指示器 */}
          <div className="carousel-indicators">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>




      {/* 公司简介与核心优势区域 */}
      <section className="company-intro-section">
        <div className="company-intro-container">
          <h2 className="section-title">{t('about.companyIntro.title')}</h2>
          <div className="company-intro-content">
            <p className="company-intro-text">
              {t('about.companyIntro.content')}
            </p>
          </div>
        </div>
      </section>

      {/* 核心优势区域 */}
      <section className="core-advantages-section">
        <div className="core-advantages-container">
          <h2 className="section-title">{t('about.coreAdvantages.title')}</h2>
          <div className="advantages-grid">
            <div className="advantage-item">
              <div className="advantage-image">
                <img src="/images/about/advantage1.webp" alt={t('about.coreAdvantages.advantage1.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">01</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage1.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage1.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item advantage-reverse">
              <div className="advantage-image">
                <img src="/images/about/advantage2.webp" alt={t('about.coreAdvantages.advantage2.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">02</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage2.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage2.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item">
              <div className="advantage-image">
                <img src="/images/about/advantage3.webp" alt={t('about.coreAdvantages.advantage3.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">03</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage3.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage3.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item advantage-reverse">
              <div className="advantage-image">
                <img src="/images/about/advantage4.webp" alt={t('about.coreAdvantages.advantage4.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">04</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage4.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage4.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item">
              <div className="advantage-image">
                <img src="/images/about/advantage5.webp" alt={t('about.coreAdvantages.advantage5.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">05</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage5.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage5.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item advantage-reverse">
              <div className="advantage-image">
                <img src="/images/about/advantage6.webp" alt={t('about.coreAdvantages.advantage6.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">06</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage6.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage6.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="advantage-item">
              <div className="advantage-image">
                <img src="/images/about/advantage7.webp" alt={t('about.coreAdvantages.advantage7.title')} />
              </div>
              <div className="advantage-content">
                <div className="advantage-number">07</div>
                <h3 className="advantage-title">{t('about.coreAdvantages.advantage7.title')}</h3>
                <ul className="advantage-list">
                  {(t('about.coreAdvantages.advantage7.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 工厂实力区域 */}
      <section id="factory-strength" className="factory-strength-section">
        <div className="factory-strength-container">
          <h2 className="section-title">{t('about.factoryStrength.title')}</h2>
          <div className="factory-strength-content">
            <div className="factory-carousel-container">
              <div className="factory-carousel-wrapper">
                {factoryImages.map((image, index) => (
                  <div
                    key={index}
                    className={`factory-carousel-slide ${index === factorySlide ? 'active' : ''}`}
                  >
                    <img src={image} alt={`${t('about.factoryStrength.title')} ${index + 1}`} />
                  </div>
                ))}
              </div>
              
              {/* 工厂轮播控制按钮 */}
              <button className="factory-carousel-btn factory-carousel-btn-prev" onClick={prevFactorySlide}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="factory-carousel-btn factory-carousel-btn-next" onClick={nextFactorySlide}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              {/* 工厂轮播指示器 */}
              <div className="factory-carousel-indicators">
                {factoryImages.map((_, index) => (
                  <button
                    key={index}
                    className={`factory-carousel-indicator ${index === factorySlide ? 'active' : ''}`}
                    onClick={() => goToFactorySlide(index)}
                  />
                ))}
              </div>
            </div>
            
            <p className="factory-description">
              {t('about.factoryStrength.description1')}
            </p>
            <p className="factory-description">
              {t('about.factoryStrength.description2')}
            </p>
            <p className="factory-description">
              {t('about.factoryStrength.description3')}
            </p>
          </div>
        </div>
      </section>

      {/* 资质证书区域 */}
      <section className="certificates-section">
        <div className="certificates-container">
          <h2 className="section-title">{t('about.certificates.title')}</h2>
          <div className="certificates-grid">
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert1.webp" alt={`${t('about.certificates.title')} 1`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert2.webp" alt={`${t('about.certificates.title')} 2`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert3.webp" alt={`${t('about.certificates.title')} 3`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert4.webp" alt={`${t('about.certificates.title')} 4`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert5.webp" alt={`${t('about.certificates.title')} 5`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert6.webp" alt={`${t('about.certificates.title')} 6`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert7.webp" alt={`${t('about.certificates.title')} 7`} />
              </div>
            </div>
            <div className="certificate-item">
              <div className="certificate-image">
                <img src="/images/about/cert8.webp" alt={`${t('about.certificates.title')} 8`} />
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default About;
